import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BrandMark from "@/components/BrandMark";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PROPERTY_TYPES = [
  { value: "apartment", label: "Apartment" },
  { value: "villa", label: "Villa / House" },
  { value: "townhouse", label: "Townhouse" },
  { value: "office", label: "Office" },
  { value: "other", label: "Other" },
];

const COUNTRIES = ["United Arab Emirates", "Saudi Arabia", "Kuwait", "Qatar", "Oman", "Bahrain", "Other"];

export default function HomeSetup() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [form, setForm] = useState({
    name: "",
    property_type: "",
    city: "",
    country: "United Arab Emirates",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Dubai",
  });
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Give your home a name.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/home", {
        name: form.name.trim(),
        property_type: form.property_type || null,
        city: form.city.trim() || null,
        country: form.country || null,
        timezone: form.timezone || null,
      });
      toast.success("Home created.");
      nav("/devices", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create home.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-bone/85 border-b border-[#E2DFD8]" data-testid="home-setup-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <BrandMark variant="light" size="sm" />
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-ink/70 hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" onClick={async () => { await logout(); nav("/"); }} className="rounded-full" data-testid="home-setup-logout">Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="flex items-center gap-3 mb-3">
          <p className="label-eyebrow">Step 1 of 2</p>
          <span className="text-[10px] font-mono uppercase tracking-widest text-ink/50">Home setup</span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.02] text-forest">
          Set up your home.
        </h1>
        <p className="mt-3 text-ink/65 leading-relaxed">
          Tell Noa about your home. You can add devices in the next step.
        </p>

        <form onSubmit={submit} className="mt-10 surface-card p-6 md:p-8 space-y-5" data-testid="home-setup-form">
          <div>
            <label className="label-eyebrow block mb-2">Home name</label>
            <Input
              required
              placeholder="e.g. Main residence"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl bg-white border-[#E2DFD8] h-12"
              data-testid="home-setup-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Property type</label>
              <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                <SelectTrigger className="rounded-xl bg-white border-[#E2DFD8] h-12" data-testid="home-setup-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">City</label>
              <Input
                placeholder="e.g. Dubai"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="rounded-xl bg-white border-[#E2DFD8] h-12"
                data-testid="home-setup-city"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Country</label>
              <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger className="rounded-xl bg-white border-[#E2DFD8] h-12" data-testid="home-setup-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Timezone</label>
              <Input
                placeholder="Asia/Dubai"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="rounded-xl bg-white border-[#E2DFD8] h-12"
                data-testid="home-setup-tz"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-forest hover:bg-forest-hover text-bone rounded-full h-12 text-base disabled:opacity-60"
            data-testid="home-setup-submit"
          >
            {submitting ? "Creating home…" : "Create home & continue"}
          </Button>
        </form>
      </main>
    </div>
  );
}
